import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  noindex?: boolean;
  schemas?: Record<string, unknown>[];
  extraSchemas?: Record<string, unknown>[];
  pageType?: string;
}

const defaultDescription = "London and South East England's trusted projector repair specialists. Expert diagnostics and precision repairs for DLP, LCD, laser and xenon projectors. 90-day warranty on all work.";

const baseSchemas = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://projectorrepairlondon.co.uk/#organization",
    "name": "Wells Electronics Ltd t/a Projector Repair London",
    "alternateName": "Projector Repair London",
    "url": "https://projectorrepairlondon.co.uk",
    "logo": {
      "@type": "ImageObject",
      "url": "https://projectorrepairlondon.co.uk/android-chrome-512x512.png",
      "width": 512,
      "height": 512
    },
    "sameAs": [
      "https://www.facebook.com/projectorrepairlondon",
      "https://twitter.com/ProjRepairLDN"
    ],
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+44-333-006-4326",
        "contactType": "customer service",
        "contactOption": "TollFree",
        "availableLanguage": ["English"],
        "areaServed": "GB",
        "hoursAvailable": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "17:00"
        }
      },
      {
        "@type": "ContactPoint",
        "telephone": "+353-1-582-7344",
        "contactType": "customer service",
        "availableLanguage": ["English"],
        "areaServed": "IE",
        "hoursAvailable": {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "17:00"
        }
      }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://projectorrepairlondon.co.uk/#localbusiness",
    "name": "Wells Electronics Ltd t/a Projector Repair London",
    "alternateName": "Projector Repair London",
    "image": "https://projectorrepairlondon.co.uk/android-chrome-512x512.png",
    "url": "https://projectorrepairlondon.co.uk",
    "telephone": "+44-333-006-4326",
    "email": "wellselectronicsltd@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Unit 9, West Moor Park, Yorkshire Way",
      "addressLocality": "Doncaster",
      "addressRegion": "England",
      "postalCode": "DN3 3GW",
      "addressCountry": "GB"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 53.5228,
      "longitude": -1.1285
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00"
      }
    ],
    "priceRange": "££",
    "currenciesAccepted": "GBP",
    "paymentAccepted": "Cash, Credit Card, Bank Transfer, Stripe, PayPal, Square",
    "areaServed": [
      { "@type": "City", "name": "London" },
      { "@type": "Place", "name": "Greater London" },
      { "@type": "Place", "name": "South East England" },
      { "@type": "Country", "name": "United Kingdom" },
      { "@type": "Country", "name": "Ireland" },
      { "@type": "Place", "name": "EMEA" }
    ],
    "department": [
      {
        "@type": "LocalBusiness",
        "name": "Projector Repair Ireland",
        "telephone": "+353-1-582-7344",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Unit 7b, Universal Business Park",
          "addressLocality": "Bluebell, Dublin",
          "postalCode": "D12",
          "addressCountry": "IE"
        }
      },
      {
        "@type": "LocalBusiness",
        "name": "Projector Repair EMEA",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Amsterdam",
          "addressCountry": "NL"
        }
      }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://projectorrepairlondon.co.uk/#website",
    "url": "https://projectorrepairlondon.co.uk",
    "name": "Wells Electronics Ltd t/a Projector Repair London",
    "publisher": { "@id": "https://projectorrepairlondon.co.uk/#organization" },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://projectorrepairlondon.co.uk/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  },
];

const serviceSchema = (type: string, desc: string): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": type,
  "provider": { "@id": "https://projectorrepairlondon.co.uk/#localbusiness" },
  "areaServed": { "@type": "Place", "name": "Greater London" },
  "description": desc
});

const faqSchema: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How long does projector repair take?",
      "acceptedAnswer": { "@type": "Answer", "text": "Most projector repairs are completed within 4-7 business days. Complex repairs may take up to 10 days." }
    },
    {
      "@type": "Question",
      "name": "Do you offer a warranty on repairs?",
      "acceptedAnswer": { "@type": "Answer", "text": "Yes, all our repairs come with a 90-day warranty covering both parts and labour." }
    },
    {
      "@type": "Question",
      "name": "What projector brands do you repair?",
      "acceptedAnswer": { "@type": "Answer", "text": "We repair all major projector brands including Epson, Optoma, BenQ, Sony, Panasonic, ViewSonic, NEC, JVC, Acer, and many more." }
    },
    {
      "@type": "Question",
      "name": "Do you offer collection and delivery?",
      "acceptedAnswer": { "@type": "Answer", "text": "Yes, we offer free same-day collection and return delivery across all London boroughs." }
    },
    {
      "@type": "Question",
      "name": "What are white dots on my projector screen?",
      "acceptedAnswer": { "@type": "Answer", "text": "White dots on a DLP projector screen are caused by a failing DMD chip. We can fix this by replacing the DLP chip." }
    }
  ]
};

const professionalServiceSchema: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "Wells Electronics Ltd t/a Projector Repair London",
  "description": "Specialist audio-visual equipment repair services with component-level expertise and precision engineering.",
  "url": "https://projectorrepairlondon.co.uk",
  "telephone": "+44-333-006-4326",
  "email": "wellselectronicsltd@gmail.com",
  "openingHours": "Mo-Fr 09:00-17:00",
  "priceRange": "££",
  "areaServed": [
    { "@type": "City", "name": "London" },
    { "@type": "Place", "name": "Greater London" },
    { "@type": "Place", "name": "South East England" }
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Projector Repair Services",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "DLP Chip Replacement" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Power & Overheating Repair" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Display Discolouration Repair" } }
    ]
  }
};

const defaultSchemas = [
  ...baseSchemas,
  serviceSchema("DLP Chip Replacement", "Expert DMD chip replacement for white dots on projector screen."),
  serviceSchema("Projector Power & Overheating Repair", "Thermal fault diagnosis and repair for overheating projectors."),
  serviceSchema("LCD Display Discolouration Repair", "LCD panel damage, colour fading, polariser fault repair."),
  faqSchema,
  professionalServiceSchema,
];

export default function SEO({
  title,
  description = defaultDescription,
  canonical = 'https://projectorrepairlondon.co.uk',
  ogImage = 'https://projectorrepairlondon.co.uk/og-image.jpg',
  noindex = false,
  schemas = defaultSchemas,
  extraSchemas = [],
  pageType,
}: SEOProps) {
  const fullTitle = title
    ? `${title} | Projector Repair London`
    : 'Projector Repair London | Expert DLP, LCD, Laser & Xenon Repairs';

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="robots"]', 'content', noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    setMeta('link[rel="canonical"]', 'href', canonical);
    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', canonical);
    setMeta('meta[property="og:type"]', 'content', pageType || 'website');
    setMeta('meta[property="og:image"]', 'content', ogImage);
    setMeta('meta[name="twitter:title"]', 'content', fullTitle);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', ogImage);

    // Inject WebPage schema for the current page
    const pageSchemas = [...schemas, ...extraSchemas];
    if (canonical !== 'https://projectorrepairlondon.co.uk') {
      pageSchemas.push({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": canonical,
        "url": canonical,
        "name": fullTitle,
        "description": description,
        "isPartOf": { "@id": "https://projectorrepairlondon.co.uk/#website" },
      });
    }

    // BreadcrumbList schema
    const pathSegments = new URL(canonical).pathname.split('/').filter(Boolean);
    if (pathSegments.length > 0) {
      const breadcrumbItems = pathSegments.map((segment, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        "item": `https://projectorrepairlondon.co.uk/${pathSegments.slice(0, i + 1).join('/')}`,
      }));
      pageSchemas.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbItems,
      });
    }

    // Remove old JSON-LD
    document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());

    // Inject new schemas
    pageSchemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    // GA4 page_view
    if (typeof window !== 'undefined' && 'gtag' in window && typeof (window as Window & { gtag?: Function }).gtag === 'function') {
      (window as Window & { gtag: Function }).gtag('event', 'page_view', {
        page_title: fullTitle,
        page_location: canonical,
      });
    }
  }, [fullTitle, description, canonical, ogImage, noindex, schemas, extraSchemas, pageType]);

  return null;
}